import { useSyncExternalStore } from 'react';

/**
 * Light / dark / system. `data-theme` on <html> carries the RESOLVED mode ("light" | "dark"), so
 * CSS only ever keys off `html[data-theme="dark"]`; the user's choice (which may be "system") is
 * what is stored.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';
export type ThemeResolved = 'light' | 'dark';

const KEY = 'hrms.theme';
const listeners = new Set<() => void>();

const read = (): ThemeChoice => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'light';
  } catch { return 'light'; }
};

const systemDark = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

/**
 * The sign-in, invite and other pre-login screens are always light: they are the product's front
 * door, branded and the same for everyone, and nobody has chosen a theme there yet. The choice a
 * person makes inside the app is stored and applies once they are signed in.
 */
const signedIn = (): boolean => {
  try { return !!localStorage.getItem('token'); } catch { return false; }
};

// Dark mode is switched off for now: everyone gets light. Restore the two lines below to re-enable.
export const DARK_MODE_ENABLED = false;
export const resolveTheme = (c: ThemeChoice): ThemeResolved => {
  if (!DARK_MODE_ENABLED) return 'light';
  if (!signedIn()) return 'light';
  return c === 'system' ? (systemDark() ? 'dark' : 'light') : c;
};

let choice: ThemeChoice = read();

const apply = (): void => {
  const el = document.documentElement;
  const r = resolveTheme(choice);
  el.setAttribute('data-theme', r);
  el.style.colorScheme = r;
};

/** Re-resolves the theme, e.g. after sign-in or sign-out changes whether the app is showing. */
export const syncTheme = (): void => { apply(); listeners.forEach((l) => l()); };

/** Call once at boot, before the first render, so there is no light flash for a dark user. */
export const initTheme = (): void => {
  apply();
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (choice === 'system') { apply(); listeners.forEach((l) => l()); }
  });
};

export const setThemeChoice = (c: ThemeChoice): void => {
  choice = c;
  try { localStorage.setItem(KEY, c); } catch { /* private mode: the choice lasts for this tab */ }
  apply();
  listeners.forEach((l) => l());
};

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

export const useThemeChoice = (): ThemeChoice => useSyncExternalStore(subscribe, () => choice, () => 'light' as ThemeChoice);
export const useResolvedTheme = (): ThemeResolved => {
  const c = useThemeChoice();
  return resolveTheme(c);
};
